import React, { useEffect, useId, useRef, useState } from "react";
import "./casefile.css";
import Icon from "../ui/Icon";
import ProjectArt from "../visuals/ProjectArt";
import BarChart from "../visuals/BarChart";
import { charts, projects, statusTone } from "./Data";

/** WAI-ARIA tabs (vertical): roving tabindex, arrow/Home/End navigation. */
const ArchitectureExplorer = ({ layers }) => {
  const uid = useId();
  const [selected, setSelected] = useState(0);
  const tabRefs = useRef([]);

  const move = (next) => {
    const i = (next + layers.length) % layers.length;
    setSelected(i);
    tabRefs.current[i]?.focus();
  };

  const onKeyDown = (e) => {
    const keys = {
      ArrowDown: selected + 1,
      ArrowRight: selected + 1,
      ArrowUp: selected - 1,
      ArrowLeft: selected - 1,
      Home: 0,
      End: layers.length - 1,
    };
    if (e.key in keys) {
      e.preventDefault();
      move(keys[e.key]);
    }
  };

  return (
    <div className="arch">
      <div className="arch__tabs" role="tablist" aria-orientation="vertical" aria-label="Architecture layers">
        {layers.map((layer, i) => (
          <button
            key={layer.name}
            ref={(el) => (tabRefs.current[i] = el)}
            type="button"
            role="tab"
            id={`${uid}-tab-${i}`}
            aria-selected={selected === i}
            aria-controls={`${uid}-panel`}
            tabIndex={selected === i ? 0 : -1}
            className="arch__tab"
            onClick={() => setSelected(i)}
            onKeyDown={onKeyDown}
          >
            <span className="arch__index" aria-hidden="true">
              {String(i + 1).padStart(2, "0")}
            </span>
            {layer.name}
          </button>
        ))}
      </div>
      <div
        className="arch__panel"
        role="tabpanel"
        id={`${uid}-panel`}
        aria-labelledby={`${uid}-tab-${selected}`}
        tabIndex={0}
      >
        <h4>{layers[selected].name}</h4>
        <p>{layers[selected].detail}</p>
      </div>
    </div>
  );
};

const Section = ({ title, children }) => (
  <section className="cf__section">
    <h3 className="cf__h">{title}</h3>
    {children}
  </section>
);

const CaseFile = ({ project, onClose }) => {
  const dialogRef = useRef(null);
  const titleId = `cf-title-${project.slug}`;

  useEffect(() => {
    const dialog = dialogRef.current;
    if (dialog && !dialog.open) dialog.showModal();
    dialog?.scrollTo(0, 0);
  }, [project.slug]);

  const { architecture, decisions, evidence, limitations, links = [] } = project;
  const hasBody = (list) => Array.isArray(list) && list.length > 0;

  return (
    <dialog
      ref={dialogRef}
      className="cf"
      aria-labelledby={titleId}
      onClose={onClose}
      onClick={(e) => e.target === dialogRef.current && dialogRef.current.close()}
    >
      <div className="cf__inner">
        <header className="cf__head">
          <div>
            <p className="eyebrow">Case file</p>
            <h2 id={titleId} className="cf__title">
              {project.title}
            </h2>
            <p className="cf__tagline">{project.tagline}</p>
            <p className="cf__meta">
              <span className={`badge badge--${statusTone[project.status]}`}>{project.status}</span>
              {project.license && <span className="tag">{project.license}</span>}
              {project.stack.map((s) => (
                <span key={s} className="tag">
                  {s}
                </span>
              ))}
            </p>
          </div>
          <button type="button" className="cf__close" onClick={() => dialogRef.current.close()} aria-label="Close case file">
            <Icon name="close" />
          </button>
        </header>

        <div className="cf__actions">
          <a className="btn btn--primary" href={project.repo} target="_blank" rel="noreferrer">
            <Icon name="github" /> View repository
            <span className="sr-only"> (opens in a new tab)</span>
          </a>
          {links.map((l) => (
            <a key={l.url} className="btn" href={l.url} target="_blank" rel="noreferrer">
              {l.label} <Icon name="external" />
              <span className="sr-only"> (opens in a new tab)</span>
            </a>
          ))}
        </div>

        <div className="frame cf__frame" aria-hidden="true">
          <div className="frame__bar">
            <span>FIG. {String(projects.findIndex((p) => p.slug === project.slug) + 1).padStart(2, "0")}</span>
            <span>{project.repoName ?? project.slug}</span>
          </div>
          <div className="frame__body cf__art">
            <ProjectArt slug={project.slug} />
          </div>
        </div>

        <Section title="The challenge">
          <p>{project.problem}</p>
        </Section>

        <Section title="Approach">
          <p>{project.approach}</p>
        </Section>

        {hasBody(architecture) && (
          <Section title="Architecture">
            <ArchitectureExplorer layers={architecture} />
          </Section>
        )}

        {hasBody(decisions) && (
          <Section title="Engineering decisions">
            <dl className="cf__defs">
              {decisions.map((d) => (
                <div key={d.title}>
                  <dt>{d.title}</dt>
                  <dd>{d.body}</dd>
                </div>
              ))}
            </dl>
          </Section>
        )}

        {charts[project.slug] && (
          <Section title="Results">
            <BarChart {...charts[project.slug]} />
          </Section>
        )}

        {hasBody(evidence) && (
          <Section title="Evidence">
            <ul className="cf__list">
              {evidence.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </Section>
        )}

        {hasBody(limitations) && (
          <Section title="Scope and next steps">
            <ul className="cf__list cf__list--warn">
              {limitations.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </Section>
        )}
      </div>
    </dialog>
  );
};

export default CaseFile;
